/**
 * Business Improvement & Efficiency — the screen. The efficiency figures,
 * the initiatives ranked by payback, and the forms for the executive's own
 * initiatives and for what a point of a KPI is worth.
 */
import { useQuery } from "@tanstack/react-query";
import { Fragment, useMemo, useState } from "react";
import { useSession } from "../../app/SessionContext";
import type { Closure } from "../../domain/closures";
import { defaultRateCard } from "../../domain/decision";
import {
  answerImprovement,
  buildImprovementDesk,
  IMPROVEMENT_MODEL_PROMPT,
  materialForImprovement,
  type ImprovementDesk,
  type ImprovementEntered,
} from "../../domain/improvement";
import { money } from "../../domain/intelligence";
import { Button, Card, ErrorState, Field, Input, Select, Skeleton } from "../../design/components";
import { t } from "../../i18n";
import { Page } from "../../layout/AppShell";
import * as store from "../decision/store";
import { useIntelligence } from "../intelligence/gather";
import { useFinance } from "./FinanceForm";
import { AskCard, Refs, Tiles, x as s } from "./shared";
import { useEntered } from "./store";
import { useScreenContext } from "../../assistant/screen";
import { InitiativeEditForm } from "../entryForms/InitiativeEdit";

function useImprovementRecords(enabled: boolean) {
  const session = useSession();
  const source = session.source;
  const siteId = session.site.id;
  return useQuery({
    queryKey: ["improvement-records", siteId],
    enabled,
    staleTime: 60_000,
    queryFn: async () => {
      const now = new Date();
      const quiet = async <T,>(p: Promise<T>, fb: T) => p.catch(() => fb);
      const [kpis, closures, rateCard] = await Promise.all([
        quiet(source.listKpis(siteId), []),
        quiet(source.listClosures(siteId, new Date(now.getTime() - 180 * 86_400_000).toISOString()), [] as Closure[]),
        store.loadRateCard(source, siteId),
      ]);
      return { kpis, closures, rateCard: rateCard ?? defaultRateCard() };
    },
  });
}

export function ImprovementPage() {
  const session = useSession();
  const gathered = useIntelligence();
  const records = useImprovementRecords(!!gathered.data);
  const finance = useFinance();
  const entered = useEntered<ImprovementEntered>("executive.improvement", {});
  /* build 6B: the entered initiative being changed (its key without the own: prefix) */
  const [editing, setEditing] = useState<string | null>(null);
  const desk = useMemo<ImprovementDesk | null>(() => {
    if (!gathered.data || !records.data) return null;
    return buildImprovementDesk({
      now: new Date(),
      siteCode: session.site.code,
      kpis: records.data.kpis,
      workOrders: gathered.data.inputs.workOrders,
      closures: records.data.closures,
      finance: finance.value,
      rateCard: records.data.rateCard,
      entered: entered.value,
    });
  }, [gathered.data, records.data, finance.value, entered.value, session.site.code]);
  useScreenContext(
    desk
      ? {
          route: "/improvement",
          title: "Business Improvement",
          summary: desk.boardLine,
          material: materialForImprovement(desk),
          figures: desk.figures,
          own: (q) => answerImprovement(q, desk),
          prompt: IMPROVEMENT_MODEL_PROMPT,
          offers: [
            { label: "What pays back first?", ask: "what pays back first?" },
            { label: "What is unpriced?", ask: "what is unpriced?" },
            { label: "Cost a passenger", ask: "what do we spend a passenger?" },
          ],
        }
      : null,
  );
  return (
    <Page
      eyebrow={t("{site} · Executive", { site: session.site.code })}
      title="Business Improvement"
      actions={
        <Button variant="primary" onClick={() => window.print()}>
          {t("Print")}
        </Button>
      }
    >
      <p className={s.lead}>
        {t(
          "Where the operation loses money, what each gap is worth, and the fixes ranked by payback.",
        )}
      </p>
      {(gathered.isPending || records.isPending) && <Skeleton rows={8} />}
      {gathered.isError && <ErrorState error={gathered.error} retry={() => void gathered.refetch()} />}
      {desk && (
        <div className={s.page}>
          <Tiles items={desk.tiles} currency={desk.currency} />
          {entered.savedTo && <p className={s.saved}>{entered.savedTo}</p>}
          <Card title="Initiatives" sub="shortest payback first; unpriced ones last, with what would price them">
            <div className={s.tableWrap}>
              <table className={s.table}>
                <thead>
                  <tr>
                    <th>{t("initiative")}</th>
                    <th>{t("metric")}</th>
                    <th>{t("a year")}</th>
                    <th>{t("cost")}</th>
                    <th>{t("payback")}</th>
                    <th>{t("status")}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {desk.initiatives.map((i) => {
                    const own = i.key.startsWith("own:")
                      ? entered.value.initiatives?.find((e) => `own:${e.key}` === i.key)
                      : undefined;
                    return (
                    <Fragment key={i.key}>
                    <tr data-testid={`initiative-${i.key}`}>
                      <td>
                        <b>{i.name}</b>
                        <div className={s.muted}>
                          {i.area}
                          {i.owner ? ` · ${i.owner}` : ""}
                        </div>
                        <div className={s.muted}>{i.valueWorking}</div>
                        <Refs list={i.sources} />
                      </td>
                      <td>
                        {i.metric
                          ? `${i.metric.label}: ${i.metric.now ?? "—"} → ${i.metric.target ?? "—"} ${i.metric.unit}`
                          : "—"}
                      </td>
                      <td className={s.num}>
                        {i.valuePerYear != null ? (
                          money(i.valuePerYear, desk.currency)
                        ) : (
                          <span className={s.blank}>{t("unpriced")}</span>
                        )}
                        {i.basis !== "computed" && <div className={s.tag}>{t(BASIS_LABEL[i.basis])}</div>}
                      </td>
                      <td className={s.num}>{i.cost != null ? money(i.cost, desk.currency) : "—"}</td>
                      <td className={s.num}>{i.paybackMonths != null ? t("{n} mo", { n: i.paybackMonths }) : "—"}</td>
                      <td>
                        <span className={i.status === "done" ? s.tagGreen : i.status === "running" ? s.tagBlue : s.tag}>
                          {t(STATUS_LABEL[i.status])}
                        </span>
                      </td>
                      <td>
                        {own && !entered.loading && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setEditing(editing === own.key ? null : own.key)}
                          >
                            {t("Change")}
                          </Button>
                        )}
                      </td>
                    </tr>
                    {own && editing === own.key && (
                      <tr>
                        <td colSpan={7}>
                          <InitiativeEditForm
                            entered={entered.value}
                            item={own}
                            onSave={(next) => entered.save(next)}
                            onDone={() => setEditing(null)}
                          />
                        </td>
                      </tr>
                    )}
                    </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
          <AskCard
            title="The line for the board"
            sub="Summary for the board, from the airport's own figures"
            board={desk.boardLine}
            material={materialForImprovement(desk)}
            figures={desk.figures}
            own={(q) => answerImprovement(q, desk)}
            prompt={IMPROVEMENT_MODEL_PROMPT}
            placeholder={t("What pays back first? What is unpriced? What do we spend a passenger?")}
          />
          <details className={s.drawer}>
            <summary>{t("Your initiatives, and what a point of a KPI is worth")}</summary>
            <div className={s.drawerBody}>
              {!entered.loading && <InitiativeForm key={entered.at ?? "seed"} entered={entered.value} onSave={(e) => void entered.save(e)} />}
            </div>
          </details>
          {desk.gaps.length > 0 && (
            <Card title="What this desk does not cover" sub="so the reader knows what is absent">
              <ul className={s.plain}>
                {desk.gaps.map((g) => (
                  <li key={g}>{g}</li>
                ))}
              </ul>
            </Card>
          )}
        </div>
      )}
    </Page>
  );
}

const BASIS_LABEL: Record<ImprovementDesk["initiatives"][number]["basis"], string> = {
  computed: "computed",
  starting: "starting",
  entered: "entered",
};
const STATUS_LABEL: Record<ImprovementDesk["initiatives"][number]["status"], string> = {
  suggested: "suggested",
  planned: "planned",
  running: "running",
  done: "done",
};

const POINT_KEYS: { key: string; label: string }[] = [
  { key: "mttr_p1_hours", label: "an hour of priority-one repair time" },
  { key: "outages", label: "one unit of major equipment out of service" },
  { key: "queue", label: "a minute off the worst queue" },
  { key: "baggage", label: "a point of bags on time" },
  { key: "punctuality", label: "a point of on-time departures" },
];

function InitiativeForm({ entered, onSave }: { entered: ImprovementEntered; onSave: (e: ImprovementEntered) => void }) {
  const [n, setN] = useState({
    name: "",
    area: "Operations",
    valuePerYear: "",
    cost: "",
    owner: "",
    status: "planned" as const,
  });
  const [pv, setPv] = useState<Record<string, string>>(
    Object.fromEntries(
      POINT_KEYS.map((p) => [p.key, entered.pointValue?.[p.key] != null ? String(entered.pointValue[p.key]) : ""]),
    ),
  );
  return (
    <div className={s.page}>
      <Card
        title="What a point is worth a year"
        sub="Targets and rates entered by the executive team"
      >
        <form
          className={s.form}
          onSubmit={(e) => {
            e.preventDefault();
            const pointValue: Record<string, number> = {};
            for (const p of POINT_KEYS) if (pv[p.key] !== "") pointValue[p.key] = Number(pv[p.key]);
            onSave({ ...entered, pointValue });
          }}
        >
          {POINT_KEYS.map((p) => (
            <Field key={p.key} label={p.label}>
              <Input
                type="number"
                min={0}
                value={pv[p.key]}
                onChange={(e) => setPv({ ...pv, [p.key]: e.target.value })}
                aria-label={t("value of {label}", { label: t(p.label) })}
              />
            </Field>
          ))}
          <Button type="submit">{t("Save the values")}</Button>
        </form>
      </Card>
      <Card title="Add an initiative" sub="ranked beside the platform's own, on the figures you give it">
        <form
          className={s.form}
          onSubmit={(e) => {
            e.preventDefault();
            if (!n.name.trim()) return;
            onSave({
              ...entered,
              initiatives: [
                ...(entered.initiatives ?? []),
                {
                  key: Date.now().toString(36),
                  name: n.name.trim(),
                  area: n.area,
                  valuePerYear: n.valuePerYear ? Number(n.valuePerYear) : undefined,
                  cost: n.cost ? Number(n.cost) : undefined,
                  owner: n.owner || undefined,
                  status: n.status,
                },
              ],
            });
            setN({ name: "", area: "Operations", valuePerYear: "", cost: "", owner: "", status: "planned" });
          }}
        >
          <Field label="name">
            <Input
              value={n.name}
              onChange={(e) => setN({ ...n, name: e.target.value })}
              aria-label={t("initiative name")}
            />
          </Field>
          <Field label="area">
            <Input
              value={n.area}
              onChange={(e) => setN({ ...n, area: e.target.value })}
              aria-label={t("initiative area")}
            />
          </Field>
          <Field label="value a year">
            <Input
              type="number"
              min={0}
              value={n.valuePerYear}
              onChange={(e) => setN({ ...n, valuePerYear: e.target.value })}
              aria-label={t("initiative value")}
            />
          </Field>
          <Field label="cost">
            <Input
              type="number"
              min={0}
              value={n.cost}
              onChange={(e) => setN({ ...n, cost: e.target.value })}
              aria-label={t("initiative cost")}
            />
          </Field>
          <Field label="owner">
            <Input
              value={n.owner}
              onChange={(e) => setN({ ...n, owner: e.target.value })}
              aria-label={t("initiative owner")}
            />
          </Field>
          <Field label="status">
            <Select
              value={n.status}
              onChange={(e) => setN({ ...n, status: e.target.value as typeof n.status })}
              aria-label={t("initiative status")}
            >
              <option value="planned">{t("planned")}</option>
              <option value="running">{t("running")}</option>
              <option value="done">{t("done")}</option>
            </Select>
          </Field>
          <Button type="submit" disabled={!n.name.trim()}>
            {t("Add it")}
          </Button>
        </form>
      </Card>
    </div>
  );
}
